import { parse, stringify } from 'telejson';

import { PersistentKeyValueStore } from './types';

// Storage is resolved per call rather than at module scope: this module is
// reachable from the workflow block config, which the node-side e2e runner
// imports, and `window` does not exist there.
const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage ?? null;
};

export const persistentKeyValueStore = (
  namespace: string,
): PersistentKeyValueStore => {
  const constructNamespacedKey = (key: string) => `${namespace}||${key}`;

  const getValueForKey = (key: string, defaultValue?: unknown) => {
    const storage = getStorage();
    if (!storage) {
      return defaultValue;
    }
    const storedValue = storage.getItem(constructNamespacedKey(key));
    if (storedValue !== null) {
      return parse(storedValue);
    }
    return defaultValue;
  };

  const setValueForKey = (key: string, value: unknown) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.setItem(constructNamespacedKey(key), stringify(value));
  };

  const deleteValueForKey = (key: string) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.removeItem(constructNamespacedKey(key));
  };

  const deleteAll = () => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    const keys = Object.keys(storage);
    keys.forEach((key) => {
      if (key.indexOf(namespace) === 0) {
        storage.removeItem(key);
      }
    });
  };

  return {
    getValueForKey,
    setValueForKey,
    deleteValueForKey,
    deleteAll,
  };
};
